import type { Cadeia, ColunaSugestao, PalavraCanonica, PalavraColocada, Sugestao } from '../tipos';
import { caracteresCanonicos, paraCanonica } from '../lexico/normalizacao';
import { filtrarEntrada, validarPalavraBase } from '../lexico/validacao';
import { mesmaCelula } from '../tabuleiro/coordenadas';
import {
  cruzamentosDe,
  multiplicadorDaAncora,
  partidaTerminou,
  pontuarJogada,
  MULTIPLICADOR_MINIMO,
} from '../modos/trama';
import {
  anunciarFimDeFase,
  anunciarNivel,
  cumpriuONivel,
  desafioDoNivel,
  faseDoNivel,
  fechaAFase,
} from '../modos/palavredo';
import { avaliar } from '../termo/avaliacao';
import { ehAResposta } from '../termo/partida';
import { anunciarMarcas } from '../termo/mensagens';
import {
  adicionarNaCadeia,
  construirGrade,
  idPalavra,
  palavrasUsadas,
  ultimaPalavra,
} from '../tabuleiro/grade';
import { ancorasElegiveis, encaixesDoDraft, origemPreview } from '../tabuleiro/posicionamento';
import type { Ambiente, EstadoJogo, Fase } from './estados';
import {
  AFASTAMENTO_PREVIEW,
  ORIGEM_INICIAL,
  estadoInicial,
  historicoDaJogada,
  podeDesfazer,
  semDraftAberto,
} from './estados';
import type { Evento } from './eventos';
import { MSG_SESSAO_RESTAURADA, anunciarCaracteresRecusados } from './mensagens';
import { registrar } from './novidade';
import type { ContextoSugestao } from './sugestoes';
import { gerarColunas, temSugestaoDisponivel } from './sugestoes';

/**
 * Redutor do jogo (§5, §6).
 *
 * `reduzir(estado, evento, ambiente)` é puro: mesma entrada, mesma saída.
 * Toda derivação — validade, encaixe, colunas de sugestão, fase — acontece em
 * `recalcular`, para que nenhum caminho de evento possa esquecer de atualizar
 * uma parte do estado.
 */

interface OpcoesRecalculo {
  /** Mantém a ocorrência da âncora escolhida à mão (T06-04). */
  readonly preservarEncaixe?: boolean;
}

/**
 * Recompõe tudo o que deriva de `(cadeia, draft, âncora, fila)`.
 * Não é chamada em `carregando`, `escolher-ancora` nem `fim`: nessas fases não
 * há draft ativo e as colunas devem permanecer como estão. No caso de `fim`,
 * recalcular também apagaria a fase terminal — o tabuleiro acabado fica como
 * está, para ser percorrido (§22.3).
 */
function recalcular(
  estado: EstadoJogo,
  ambiente: Ambiente,
  opcoes: OpcoesRecalculo = {},
): EstadoJogo {
  if (semDraftAberto(estado)) return estado;

  const grade = construirGrade(estado.cadeia);
  const { ancora } = estado;

  // R5-15: uma volta pela cadeia por recálculo, e o mesmo conjunto serve à
  // validação do que o jogador escreve e à escolha do que o jogo sugere.
  const usadas = palavrasUsadas(estado.cadeia);

  const base = paraCanonica(estado.draft);
  const tamanho = caracteresCanonicos(base).length;

  let origemDraft = ORIGEM_INICIAL;
  let draftProvisorio = false;
  let encaixes = estado.encaixes;
  let encaixeAtivo = opcoes.preservarEncaixe === true ? estado.encaixeAtivo : 0;

  if (ancora !== undefined) {
    encaixes = encaixesDoDraft(grade, estado.draft, ancora, ambiente.indice);
    const ativo = encaixes[encaixeAtivo] ?? encaixes[0];

    if (ativo !== undefined && ativo.valido) {
      origemDraft = ativo.origem;
      encaixeAtivo = encaixes.indexOf(ativo);
    } else {
      // T05: o draft ainda não contém a âncora (ou nenhuma ocorrência cabe).
      // Ele flutua preso à célula escolhida, sem ocupar células permanentes.
      draftProvisorio = true;
      encaixeAtivo = 0;
      origemDraft = origemPreview(grade, ancora.celula, tamanho, AFASTAMENTO_PREVIEW);
    }
  }

  let colunas: readonly ColunaSugestao[] = [];
  if (tamanho > 0) {
    const palavraDraft: PalavraColocada = {
      id: 'draft',
      texto: base,
      origem: origemDraft,
      orientacao: 'horizontal',
      papel: 'base',
      turno: estado.cadeia.palavras.length,
    };

    // A grade das sugestões precisa conter o próprio draft: é nele que a
    // vertical cruza, e são as células dele que ela não pode invadir. A cadeia
    // confirmada sozinha não sabe onde o draft está.
    //
    // No preview provisório de T05 a faixa não ocupa células permanentes
    // (T05-02), então as sugestões provisórias são calculadas contra uma grade
    // só com o draft: cada letra continua recebendo sugestão em tempo real
    // (T05-03) mesmo com a faixa desenhada perto da cadeia. As posições
    // definitivas só existem depois do encaixe (§5.12).
    const cadeiaComDraft: Cadeia = {
      palavras: draftProvisorio ? [palavraDraft] : [...estado.cadeia.palavras, palavraDraft],
    };

    const contexto: ContextoSugestao = {
      indice: ambiente.indice,
      grade: construirGrade(cadeiaComDraft),
      sementeSessao: estado.semente,
      fila: estado.fila,
      base,
      origemBase: origemDraft,
      celulasJaCruzadas: draftProvisorio ? [] : estado.celulasJaCruzadas,
      usadas,
    };
    colunas = gerarColunas(contexto);
  }

  const fase = decidirFase(estado, ambiente, {
    tamanho,
    draftProvisorio,
    temSugestao: temSugestaoDisponivel(colunas),
    usadas,
  });

  return { ...estado, fase, origemDraft, draftProvisorio, encaixes, encaixeAtivo, colunas };
}

function decidirFase(
  estado: EstadoJogo,
  ambiente: Ambiente,
  ctx: {
    tamanho: number;
    draftProvisorio: boolean;
    temSugestao: boolean;
    usadas: ReadonlySet<PalavraCanonica>;
  },
): Fase {
  const ancorado = estado.ancora !== undefined;

  if (ctx.tamanho === 0) return ancorado ? 'draft-ancorado' : 'inicio';
  if (ancorado && ctx.draftProvisorio) return 'draft-ancorado';

  const resultado = validarPalavraBase(
    estado.draft,
    ambiente.indice,
    estado.ancora?.caractere,
    ctx.usadas,
  );
  // R5-15: repetida tem fase própria porque tem conserto próprio. Cair em
  // `digitando` mandaria o jogador procurar erro numa palavra que ele escreveu
  // certa — o problema não é a palavra, é o tabuleiro já tê-la.
  if (!resultado.valido) {
    return resultado.motivo === 'ja-no-tabuleiro' ? 'repetida' : 'digitando';
  }

  // Palavra reconhecida: com cruzamento vai para T03; sem cruzamento, T08.
  return ctx.temSugestao ? 'palavra-valida' : 'sem-cruzamento';
}

function sugestaoPorId(colunas: readonly ColunaSugestao[], id: string): Sugestao | undefined {
  for (const coluna of colunas) {
    if (coluna.tipo === 'sugestao' && coluna.sugestao.id === id) return coluna.sugestao;
  }
  return undefined;
}

/** Toda sugestão exibida entra na fila de novidade (§4.2). */
function registrarExibidas(estado: EstadoJogo): EstadoJogo['fila'] {
  let fila = estado.fila;
  for (const coluna of estado.colunas) {
    if (coluna.tipo === 'sugestao') fila = registrar(fila, coluna.sugestao.texto);
  }
  return fila;
}

export function reduzir(estado: EstadoJogo, evento: Evento, ambiente: Ambiente): EstadoJogo {
  switch (evento.tipo) {
    case 'dicionario-pronto': {
      if (estado.fase !== 'carregando') return estado;
      const semFalha: EstadoJogo = { ...estado, fase: 'inicio' };
      delete (semFalha as { falhaDicionario?: unknown }).falhaDicionario;
      return recalcular(semFalha, ambiente);
    }

    case 'dicionario-falhou': {
      const falha =
        evento.detalhe === undefined
          ? { mensagem: evento.mensagem }
          : { mensagem: evento.mensagem, detalhe: evento.detalhe };
      return { ...estado, fase: 'carregando', falhaDicionario: falha };
    }

    case 'sessao-restaurada': {
      const restaurado: EstadoJogo = { ...evento.estado, anuncio: MSG_SESSAO_RESTAURADA };
      return recalcular(restaurado, ambiente, { preservarEncaixe: true });
    }

    case 'editar-draft': {
      // Sem draft aberto não há texto a receber: nem carregando, nem na
      // escolha da âncora, nem com a partida encerrada (§22.3, T11-06).
      if (semDraftAberto(estado)) return estado;

      const filtrada = filtrarEntrada(evento.texto);
      const anuncio = anunciarCaracteresRecusados(filtrada.recusados);
      const proximo: EstadoJogo = { ...estado, draft: filtrada.texto };
      if (anuncio.length > 0) {
        return recalcular({ ...proximo, anuncio }, ambiente);
      }
      return recalcular(proximo, ambiente);
    }

    case 'escolher-sugestao': {
      // §5.5: só é selecionável quando a palavra-base é válida e há sugestão.
      if (estado.fase !== 'palavra-valida') return estado;

      const sugestao = sugestaoPorId(estado.colunas, evento.id);
      if (sugestao === undefined) return estado;

      const turnoBase = estado.cadeia.palavras.length;
      const palavraBase: PalavraColocada = {
        id: idPalavra(turnoBase, 'horizontal'),
        texto: paraCanonica(estado.draft),
        origem: estado.origemDraft,
        orientacao: 'horizontal',
        papel: 'base',
        turno: turnoBase,
      };
      const ponte: PalavraColocada = {
        id: idPalavra(turnoBase + 1, 'vertical'),
        texto: sugestao.texto,
        origem: sugestao.origem,
        orientacao: 'vertical',
        papel: 'ponte',
        turno: turnoBase + 1,
      };

      // §22.3: as travessias contam contra a grade de **antes** da jogada, e
      // cobram o multiplicador apostado ao escolher a âncora que abriu ela.
      const jogada = pontuarJogada(
        caracteresCanonicos(palavraBase.texto).length,
        cruzamentosDe(construirGrade(estado.cadeia), palavraBase),
        estado.multiplicador,
      );

      const cadeia = adicionarNaCadeia(adicionarNaCadeia(estado.cadeia, palavraBase), ponte);

      // §22.4: o Átrio tem número fixo de jogadas. No Bistrô `partidaTerminou`
      // nunca é consultado e a cadeia segue sem fim.
      //
      // §23: na Trama a mesma palavra que acabou de ser confirmada é
      // também o palpite do dia, e escrevê-la encerra a partida. Não há teto
      // de tentativas: com a âncora mandando na letra obrigatória, um teto
      // fixo tornaria alguns dias impossíveis por posicionamento.
      const achouAPalavra =
        estado.modo === 'termo' && ehAResposta(palavraBase.texto, estado.resposta);

      // §13: as marcas da Trama são cor; o anúncio as diz por extenso.
      const anuncioTermo =
        estado.modo === 'termo' && estado.resposta !== undefined
          ? anunciarMarcas(palavraBase.texto, avaliar(palavraBase.texto, estado.resposta))
          : undefined;

      /*
       * §22.7: o nível é conferido depois da jogada, contra a cadeia que ela
       * acabou de formar. É aqui, e não na interface, porque subir de nível é
       * regra de jogo — a interface só desenha o que o motor decidiu.
       *
       * O arcade não acaba: o `fim` que ele alcança é a pausa entre fases, e
       * `comecar-fase` sai dela. O que muda a cada nível é o desafio; o que
       * muda a cada fase é também o tabuleiro.
       */
      const nivel = estado.nivel ?? 1;
      const desde = estado.nivelDesde ?? 0;
      const subiu =
        estado.modo === 'palavredo' && cumpriuONivel(cadeia, estado.semente, nivel, desde);

      /*
       * Cumprir o último nível da fase não sobe o nível aqui: para em `fim`,
       * com o tabuleiro inteiro de pé. O nível só anda quando o jogador pede a
       * fase seguinte (`comecar-fase`), e é lá que a cadeia é apagada — entre
       * uma coisa e outra ele ainda quer ver o que construiu e baixá-lo (§7.5).
       */
      const fechouFase = subiu && fechaAFase(nivel);
      const arcade =
        subiu && !fechouFase ? { nivel: nivel + 1, nivelDesde: cadeia.palavras.length } : undefined;

      const anuncioArcade = fechouFase
        ? anunciarFimDeFase(faseDoNivel(nivel))
        : subiu
          ? anunciarNivel(desafioDoNivel(estado.semente, nivel + 1))
          : undefined;

      // A Trama e o Palavredo nunca coexistem: cada anúncio é do seu modo.
      const anuncio = anuncioTermo ?? anuncioArcade;

      const acabou =
        achouAPalavra || (estado.modo === 'trama' && partidaTerminou(cadeia)) || fechouFase;

      // §5.7: todas as demais sugestões desaparecem. §5.8: a ponte fica
      // selecionável, exceto a interseção já usada.
      return {
        ...estado,
        fase: acabou ? 'fim' : 'escolher-ancora',
        pontos: estado.pontos + jogada.pontos,
        ultimaJogada: jogada,
        // A aposta foi cobrada; a próxima nasce na escolha da âncora seguinte.
        multiplicador: MULTIPLICADOR_MINIMO,
        cadeia,
        draft: '',
        colunas: [],
        encaixes: [],
        encaixeAtivo: 0,
        ...(arcade ?? {}),
        ...(anuncio === undefined ? {} : { anuncio }),
        fila: registrarExibidas(estado),
        intersecaoDaPonte: sugestao.cruzamento,
        ancora: undefined,
        celulasJaCruzadas: [],
        // §11: quanto dessa jogada dá para voltar é decisão do modo.
        anterior: historicoDaJogada(estado),
      };
    }

    case 'escolher-ancora': {
      if (estado.fase !== 'escolher-ancora') return estado;

      const ponte = ultimaPalavra(estado.cadeia);
      if (ponte === undefined || ponte.papel !== 'ponte') return estado;

      const elegiveis = ancorasElegiveis(
        ponte,
        estado.intersecaoDaPonte,
        ambiente.indice,
        construirGrade(estado.cadeia),
      );
      const ancora = elegiveis.find((a) => mesmaCelula(a.celula, evento.celula));
      if (ancora === undefined) return estado;

      return recalcular(
        {
          ...estado,
          fase: 'draft-ancorado',
          ancora,
          // §22.3: escolher a letra é apostar. O multiplicador fica valendo
          // para a palavra que vier — e some junto com ela se não vier.
          multiplicador: multiplicadorDaAncora(ambiente.indice, ancora.caractere),
          draft: '',
          // §5.13: a célula da âncora já foi atravessada pela ponte e não
          // recebe uma segunda vertical.
          celulasJaCruzadas: [ancora.celula],
          encaixes: [],
          encaixeAtivo: 0,
          // §11: a escolha da letra sempre volta atrás — em todo modo. O que
          // vem depois dela é o histórico que a jogada deixou, e esse é o que
          // varia: no Bistrô a cadeia inteira, na Trama e no Palavredo nada.
          anterior: estado,
        },
        ambiente,
      );
    }

    case 'alternar-encaixe': {
      if (estado.ancora === undefined || estado.encaixes.length < 2) return estado;

      const validos = estado.encaixes
        .map((encaixe, i) => ({ encaixe, i }))
        .filter(({ encaixe }) => encaixe.valido);
      if (validos.length < 2) return estado;

      const atual = validos.findIndex(({ i }) => i === estado.encaixeAtivo);
      const alvo = validos[(atual + evento.direcao + validos.length) % validos.length];
      if (alvo === undefined) return estado;

      return recalcular({ ...estado, encaixeAtivo: alvo.i }, ambiente, { preservarEncaixe: true });
    }

    case 'escolher-encaixe': {
      if (estado.ancora === undefined) return estado;

      const alvo = estado.encaixes.findIndex(
        (encaixe) => encaixe.valido && encaixe.indiceNoDraft === evento.indiceNoDraft,
      );
      if (alvo === -1 || alvo === estado.encaixeAtivo) return estado;

      return recalcular({ ...estado, encaixeAtivo: alvo }, ambiente, { preservarEncaixe: true });
    }

    case 'desfazer': {
      // A mesma condição que a interface consulta decide aqui: `Ctrl+Z` não
      // tem botão para ficar desabilitado, então sem esta guarda ele desfaria
      // uma partida do Átrio já encerrada (§22.4, T11-06).
      if (!podeDesfazer(estado)) return estado;

      // T05-07 / §11: com draft não vazio, desfazer age primeiro sobre o texto.
      // O campo é controlado pelo React e não tem histórico nativo, então é o
      // motor que desfaz a edição: o draft volta ao vazio com que foi aberto.
      // Ver ADR-0006.
      if (estado.draft.length > 0) {
        return recalcular({ ...estado, draft: '' }, ambiente, { preservarEncaixe: true });
      }
      // Com draft vazio, a ação global desfaz a jogada ou a escolha de âncora.
      if (estado.anterior === undefined) return estado;
      return recalcular({ ...estado.anterior }, ambiente, { preservarEncaixe: true });
    }

    case 'recomecar': {
      // Modo omitido significa "de novo, igual": só a troca explícita de modo
      // muda as regras debaixo do jogador. A duração segue a mesma regra, para
      // que `Jogar de novo` numa corrida de 30 s não vire uma de 60 s.
      const modo = evento.modo ?? estado.modo;
      const duracao =
        evento.duracaoSegundos ?? (modo === estado.modo ? estado.duracaoSegundos : undefined);
      const base: EstadoJogo = { ...estadoInicial(evento.semente, modo, duracao), fase: 'inicio' };

      // §23.1: recomeçar na Trama limpa o tabuleiro, mas o dia é o
      // mesmo — a palavra de hoje não muda porque alguém desistiu do tabuleiro.
      if (modo !== 'termo' || estado.modo !== 'termo' || estado.dia === undefined) return base;
      return {
        ...base,
        dia: estado.dia,
        ...(estado.resposta === undefined ? {} : { resposta: estado.resposta }),
      };
    }

    case 'continuar-cadeia': {
      // §22.6: só de uma partida encerrada, e só quando a cadeia termina numa
      // ponte — é a letra dela que abre a jogada seguinte. Sem ponte não há
      // por onde continuar, e voltar a `inicio` com o tabuleiro cheio poria a
      // próxima palavra por cima da primeira.
      if (estado.fase !== 'fim') return estado;
      const ponte = ultimaPalavra(estado.cadeia);
      if (ponte === undefined || ponte.papel !== 'ponte') return estado;

      const proximo: EstadoJogo = {
        ...estado,
        modo: evento.modo,
        fase: 'escolher-ancora',
        draft: '',
        colunas: [],
        encaixes: [],
        encaixeAtivo: 0,
        ancora: undefined,
        // O placar era da partida que acabou; a cadeia continua, o placar não.
        pontos: 0,
        multiplicador: MULTIPLICADOR_MINIMO,
        // §11: a partida encerrada não se desfaz, e continuar não reabre o
        // histórico dela — desfazer aqui ressuscitaria o modo anterior.
        anterior: undefined,
      };

      // O que era do modo que acabou não atravessa: a palavra do dia, o prazo
      // da corrida e a duração dela não significam nada no modo seguinte.
      const solto = proximo as {
        dia?: string;
        resposta?: PalavraCanonica;
        terminaEm?: number;
        duracaoSegundos?: number;
        ultimaJogada?: unknown;
        nivel?: number;
        nivelDesde?: number;
      };
      delete solto.dia;
      delete solto.resposta;
      delete solto.terminaEm;
      delete solto.duracaoSegundos;
      delete solto.ultimaJogada;
      delete solto.nivel;
      delete solto.nivelDesde;

      // §22.7: seguir no Palavredo abre o arcade no nível 1, contando do ponto
      // em que a cadeia está — o tabuleiro que veio de antes é herança, não
      // progresso já feito.
      if (evento.modo !== 'palavredo') return proximo;
      return { ...proximo, nivel: 1, nivelDesde: estado.cadeia.palavras.length };
    }

    case 'dia-pronto': {
      // §23.2: só a Trama tem dia. Chegar o mesmo dia de novo não mexe
      // na partida em curso; chegar outro começa a do dia novo, que é o que
      // acontece com a aba deixada aberta na virada.
      if (estado.modo !== 'termo') return estado;
      if (estado.dia === evento.dia && estado.resposta === evento.resposta) return estado;
      if (estado.dia === undefined || estado.dia === evento.dia) {
        return recalcular({ ...estado, dia: evento.dia, resposta: evento.resposta }, ambiente);
      }
      return {
        ...estadoInicial(estado.semente, 'termo'),
        fase: 'inicio',
        dia: evento.dia,
        resposta: evento.resposta,
      };
    }

    case 'iniciar-corrida': {
      // §22.2: o prazo não se estica. Só a primeira letra o define, e só num
      // Nitro que ainda não começou a contar.
      if (estado.modo !== 'corre' || estado.fase === 'fim') return estado;
      if (estado.terminaEm !== undefined) return estado;
      return { ...estado, terminaEm: evento.terminaEm };
    }

    case 'tempo-esgotado': {
      // Quem tem o relógio é quem despacha (ADR-0001); aqui só se encerra.
      if (estado.modo !== 'corre' || estado.fase === 'fim') return estado;
      // A palavra em curso não conta: só jogada confirmada põe palavra no
      // tabuleiro, e o tempo acabou antes de esta ser confirmada.
      return { ...estado, fase: 'fim', draft: '', colunas: [] };
    }

    case 'comecar-fase': {
      // §22.7: só do arcade, e só da pausa entre fases. Noutro modo `fim` é fim
      // de verdade, e limpar o tabuleiro ali apagaria o resultado da partida.
      if (estado.modo !== 'palavredo' || estado.fase !== 'fim') return estado;

      const proximo = (estado.nivel ?? 1) + 1;
      return recalcular(
        {
          // Tabuleiro novo, semente velha: os desafios das fases seguintes já
          // estão determinados por ela (ADR-0004), e trocá-la aqui reescreveria
          // o futuro de uma partida em curso.
          ...estadoInicial(estado.semente, 'palavredo'),
          fase: 'inicio',
          nivel: proximo,
          nivelDesde: 0,
          anuncio: anunciarNivel(desafioDoNivel(estado.semente, proximo)),
        },
        ambiente,
      );
    }

    case 'anuncio-consumido': {
      if (estado.anuncio === undefined) return estado;
      const proximo = { ...estado };
      delete (proximo as { anuncio?: string }).anuncio;
      return proximo;
    }
  }
}
